'use strict';

var extend = require('../util/extend');
var SVPCustomEvent = require('../util/custom-event');
import * as DomUtils from '../util/dom';
import * as UrlUtil from '../util/url';
var device = require('../util/device');
var VideoUtil = require('../util/video-attributes');
var config = require('../config');
var Debug = require('../util/debug')('dash-video');
var DashDebug = require('../util/debug')('dashjs');
var AudioTrack = require('../constants/audio-track');


module.exports = (function () {

	// Structure is {mediaPlayer: <dash.MediaPlayer>, originalAttributes: <Array>}
	let instances = {};
    const AUDIO_STORAGE_KEY = "audioTrack";


    // TODO: This is due to a bug in dashjs, should hopefully be fixed in 2.3.0
    let dvrWindowSafetyMarginSeconds = 8;

	function _extend(videoElement, uid) {
		extend(videoElement, {
			getDashInstances: function () {
				return instances;
			},
            getSrc() {
                // TODO: Use videoElement.src when we fetch url from redirector directly
                //       and set as src
                return instances[uid].manifest.originalUrl;
            },
			getCurrentTime: function () {
				return instances[uid].mediaPlayer.time();
			},
			getDuration: function () {
				return videoElement.liveDuration ? Math.max(videoElement.liveDuration, instances[uid].mediaPlayer.time()) : instances[uid].mediaPlayer.duration();
			},
			getVolume: function () {
				return instances[uid].mediaPlayer.getVolume();
			},
            isPaused() {
                // Seeked event is sent for live dash stream before player is initialized
                return instances[uid] && instances[uid].mediaPlayer.isPaused();
            },
            isSeeking() {
                return instances[uid] && instances[uid].mediaPlayer.isSeeking();
            },
			isMuted: function () {
				return instances[uid] && instances[uid].mediaPlayer.isMuted();
			},
			mute: function (shouldMute) {
				instances[uid].mediaPlayer.setMute(shouldMute);
			},
			seek: function (position) {
				var sanePosition = videoElement.liveDuration ? Math.min(instances[uid].mediaPlayer.getDVRWindowSize() - dvrWindowSafetyMarginSeconds, position) : position;
				instances[uid].mediaPlayer.seek(sanePosition);
			},
			getMaxSeekable: function () {
				return instances[uid].mediaPlayer.getDVRWindowSize() - dvrWindowSafetyMarginSeconds;
			},
			setVolume: function (volume) {
				instances[uid].mediaPlayer.setVolume(volume);
			},
			showCaptions: function (shouldShow) {
				videoElement.parentNode.dispatchEvent(new SVPCustomEvent('captionsvisible', { detail: { shouldShow: shouldShow }}));
            },
            getTextTracks: function () {
                return [];
            },
            setAudioTrack: function (audioTrack) {
                let tracks = instances[uid].mediaPlayer.getTracksFor('audio');
                for(let i = 0; i < tracks.length; i++) {
                    if (tracks[i].lang === audioTrack) {
                        // important to run setStoredData before setCurremtTrack
                        // setCurrentTrack will trigger audiotracksloaded event
                        DomUtils.setStoredData(AUDIO_STORAGE_KEY, audioTrack);
                        instances[uid].mediaPlayer.setCurrentTrack(tracks[i]);
                    }
                }
            },
            getAudioTracks: function () {
                let activeAudioTrack = DomUtils.getStoredData(AUDIO_STORAGE_KEY) || AudioTrack.DEFAULT_AUDIO;

			    return instances[uid].mediaPlayer.getTracksFor('audio')
                    .map((track) => {
                        return {
                            id: track.lang,
                            active: track.lang === activeAudioTrack
                        };
                    });
            },
            getMetrics: function () {
                const mediaPlayer = instances[uid].mediaPlayer;
                const manifest = instances[uid].manifest;
                const metrics = mediaPlayer.getMetricsFor('video');
                const dashMetrics = mediaPlayer.getDashMetrics();
                const currentDroppedFrames = dashMetrics.getCurrentDroppedFrames(metrics);
                const isSVTCdn = manifest.originalUrl.indexOf('cdn.svt') > 0;

                return {
                    droppedFrames: currentDroppedFrames ? currentDroppedFrames.droppedFrames : 0,
                    bufferLevel: dashMetrics.getCurrentBufferLevel(metrics),
                    cdn: isSVTCdn ? 1 : 2,
                    dimensions: `${Math.round(videoElement.offsetWidth)} x ${Math.round(videoElement.offsetHeight)}`,
                    resolution: `${videoElement.videoWidth} x ${videoElement.videoHeight}`,
                    dash: true
                };
            },
			reset: function () {
				// if (videoElement.currentTime) {
				// 	videoElement.currentTime = 0.01; //Bug in dash 2.0 when seeking to 0 in some streams
				// }
				instances[uid].mediaPlayer.seek(0);
			},
			unload: function () {
				if (instances[uid]) {

					instances[uid].mediaPlayer.reset();

					instances[uid].mediaPlayer = undefined;
				}

                if (videoElement) {
                    while (videoElement.lastChild) {
                        videoElement.removeChild(videoElement.lastChild);
                    }

                    videoElement.removeAttribute('src');
                }

                delete instances[uid];
            }
        });
    }

    return {
        load: function (videoId, videoElement, videoUrl, metadata, videoConfig, api, dashjs) {
            const maxBitrate = VideoUtil.getMaxBitrate(videoElement);
            const uid = VideoUtil.getUID(videoElement) || VideoUtil.generateAndSetUID(videoElement);

            let dashObject = instances[uid];

            if (dashObject) {
                dashObject.mediaPlayer.reset();
                dashObject.mediaPlayer = undefined;
                videoElement.removeAttribute('crossorigin');
                videoElement.removeAttribute('src');

                while (videoElement.lastChild) {
                    videoElement.removeChild(videoElement.lastChild);
                }
            } else {
                dashObject = {
                    originalAttributes: DomUtils.getAttributes(videoElement)
                };
            }

            videoElement.setAttribute('data-video-uid', uid);

			const mediaPlayer = dashjs.MediaPlayer().create();
			dashObject.mediaPlayer = mediaPlayer;

            // Pipe dash.js logging to our log utility.
            mediaPlayer.getDebug().setLogToBrowserConsole(false);
			mediaPlayer.on(dashjs.MediaPlayer.events.LOG, function (e) {
				DashDebug.log(e.message);
			});
			// Customized the dash.js VideoModel to use window dimensions.
            // By using the window dimensions the user can the best quality
            // when going fullscreen, presuming bandwidth is not the limiting
            // factor.
            mediaPlayer.extend('VideoModel', () => ({
                getClientWidth: () => window.outerWidth,
                getClientHeight: () => window.outerHeight
            }), true);

            var canPlayListener = function () {
                var startPosition = VideoUtil.getStartPosition(videoElement);
                if (startPosition > 0) {
                    videoElement.currentTime = startPosition;
                }
                videoElement.removeEventListener('canplay', canPlayListener);
            };

            videoElement.addEventListener('canplay', canPlayListener);

			var manifestLoadedListener = function (event) {
				dashObject.manifest = event.data;
				mediaPlayer.setLimitBitrateByPortal(videoConfig.limitBitrateByPortal);
				mediaPlayer.setUsePixelRatioInLimitBitrateByPortal(true);
				mediaPlayer.setFastSwitchEnabled(true);
				if (maxBitrate) {
					mediaPlayer.setMaxAllowedBitrateFor('video', maxBitrate);
				} else {
					mediaPlayer.setMaxAllowedBitrateFor('video', NaN);
				}

                let audioPreference = DomUtils.getStoredData(AUDIO_STORAGE_KEY);
                if (audioPreference) {
                    mediaPlayer.setInitialMediaSettingsFor('audio', {
                        lang: audioPreference
                    });
                }

                mediaPlayer.off(dashjs.MediaPlayer.events.MANIFEST_LOADED, manifestLoadedListener);
                api.fire('dashmanifestloaded', (event || {}).data);
            };

            mediaPlayer.on(dashjs.MediaPlayer.events.STREAM_INITIALIZED, () => {
                let tracks = mediaPlayer.getTracksFor('audio');
                Debug(`found ${tracks.length} audio tracks`);

                if (tracks.length > 1) {
                    for (let i = 0; i < tracks.length; i++) {
                        if (tracks[i].lang === AudioTrack.SPOKEN_TEXT) {
                            api.fire('audiotracksloaded');
                            break;
                        }
                    }
                }
            });

			mediaPlayer.on(dashjs.MediaPlayer.events.MANIFEST_LOADED, manifestLoadedListener);

            mediaPlayer.on(dashjs.MediaPlayer.events.ERROR, function (event) {
                api.fire('error', {
                    player: 'dash',
                    name: event.error,
                    id: event.event.id,
                    url: event.event.url
                });
            });

            mediaPlayer.on(dashjs.MediaPlayer.events.BUFFER_EMPTY, function () {
                api.fire('bufferEmpty');
            });

            //Temporary hack for the new cdn switch, remove this when stream team are ready for primetime
            var altUrl = UrlUtil.getQueryParameter('alt', videoUrl);
            if (config.useAltDashUrl && altUrl && device.isRedirectCapable()) {
                videoUrl = decodeURIComponent(altUrl);
            }

            videoElement.liveDuration = metadata && metadata.timeshiftEnabled ? metadata.contentDuration : undefined;

            const autoplay = api.isAutoplay();

            // In some cases autoplay is not allowed if the playback is not initiated by a user gesture (for instance, a click).
            // To make it possible to handle this error, we will listen on the `PLAYBACK_NOT_ALLOWED event
            // and pass by an `autoplaynotallowed` event. If the video element has an `muted` attribute,
            // the support for autoplay will increase.
            //
            // For more info about support for autoplay in combination with the `muted` attribute:
            // https://developer.jwplayer.com/articles/html5-report/tag-attributes-and-apis/autoplay-loop-muted.html

            if (autoplay) {
                mediaPlayer.on(dashjs.MediaPlayer.events.PLAYBACK_NOT_ALLOWED, () => {
                    Debug('Playback error', 'Playback must probably be initiated by a user gesture.');
                    api.fire('autoplaynotallowed');
                });
            }

            mediaPlayer.on(dashjs.MediaPlayer.events.QUALITY_CHANGE_REQUESTED, (e) => {
                const bitrateInfoList = mediaPlayer.getBitrateInfoListFor('video');

                // Find new bitrate
                let newBitrate;
                for (let i = 0; i < bitrateInfoList.length; i++) {
                    if (bitrateInfoList[i].qualityIndex === e.newQuality) {
                        newBitrate = bitrateInfoList[i].bitrate;
                        break;
                    }
                }

                if (newBitrate) {
                    api.fire('bitrateswitch', { mediaType: e.mediaType, bitrate: newBitrate });
                }
            });

            Debug(`Init dash.js, url = ${videoUrl} , autoplay = ${autoplay}`);
            mediaPlayer.initialize(videoElement, videoUrl, autoplay);

            instances[uid] = dashObject;

            _extend(videoElement, uid);

            return videoElement;
        }
    };
})();



// WEBPACK FOOTER //
// ./src/js/modules/dash-video.js